import React, { useState } from 'react';
import { formatPercent } from '@/lib/format';
import type { BudgetType } from '@/lib/constants';
import { budgetGoalInputLabel, budgetGoalSliderLabel, budgetTypeHelp, translateBudgetType } from '@/lib/translations';
import { cn } from '@/lib/utils';
import { BudgetGoalSlider } from './BudgetGoalSlider';
import { GOAL_SHADES } from './goalShades';

export interface BudgetGoalRowProps {
  type: BudgetType;
  /** The goal in hundredths of a percent, as it would be saved. */
  hundredths: number;
  onChange: (hundredths: number) => void;
}

// "12,5" or "12.5" or "12,5%" → 1250; anything else (empty, letters, out of range) → null, and the field reverts.
const parsePercentInput = (text: string): number | null => {
  const cleaned = text.replace('%', '').trim().replace(',', '.');
  if (!/^\d{1,3}(\.\d{0,2})?$/.test(cleaned)) return null;
  const hundredths = Math.round(parseFloat(cleaned) * 100);
  return hundredths >= 0 && hundredths <= 10000 ? hundredths : null;
};

// The figure as the field shows it while not being edited: pt-BR, no % sign (the sign sits beside the field).
const toFieldText = (hundredths: number): string => formatPercent(hundredths / 100).replace('%', '');

// One bucket of the goal set: its name and what it is for, the percentage — typed or slid — and the slider.
// Percentages only. What a goal is worth in reais is the backend's `must_spend` — earmarks and deductions
// included — and it is shown on Orçamento; a second, client-side version of that formula here could only
// disagree with it (§10.8). The swatch matches the group's segment in the split bar above.
export const BudgetGoalRow: React.FC<BudgetGoalRowProps> = ({ type, hundredths, onChange }) => {
  const name = translateBudgetType(type);
  const percent = formatPercent(hundredths / 100);
  // While typing the field holds the text as typed; otherwise it shows the value.
  const [typing, setTyping] = useState<string | null>(null);

  const commit = () => {
    if (typing === null) return;
    const next = parsePercentInput(typing);
    if (next !== null && next !== hundredths) onChange(next);
    setTyping(null);
  };

  return (
    <li className="py-4">
      <div className="flex items-start justify-between gap-x-4">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
            <span className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-sm', GOAL_SHADES[type])} aria-hidden="true" />
            {name}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{budgetTypeHelp(type)}</p>
        </div>
        {/* A second way to the same value, for an exact number: Enter or leaving the field commits it. The label is
            the field's box (border on hover, ring on focus), so the % sits right against the digits; a text field
            always shows its focus, so focus-within is the same signal as the input's focus-visible. */}
        <label className="flex shrink-0 cursor-text items-baseline rounded-sm border border-transparent px-1 py-0.5 text-sm font-semibold tabular-nums text-foreground hover:border-border focus-within:border-border focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
          <input
            type="text"
            inputMode="decimal"
            aria-label={budgetGoalInputLabel(name)}
            value={typing ?? toFieldText(hundredths)}
            onFocus={(event) => {
              setTyping(toFieldText(hundredths));
              event.currentTarget.select();
            }}
            onChange={(event) => setTyping(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commit();
              if (event.key === 'Escape') setTyping(null);
            }}
            className="w-12 bg-transparent p-0 text-right focus-visible:outline-none"
          />
          <span aria-hidden="true">%</span>
        </label>
      </div>

      <BudgetGoalSlider
        className="mt-1"
        value={hundredths}
        onValueChange={onChange}
        label={budgetGoalSliderLabel(name)}
        valueText={percent}
      />
    </li>
  );
};
